import { type OfferPageHighlightTagContext } from './useOfferPageHighlightTags'

const PROPERTY_INCLUSION_SYMBOL_CATEGORY_MAP: Partial<Record<App.OfferFeatureSymbol, App.PackageInclusionCategory>> = {
  childs_face: 'One Child Stays Free',
  childs_face_with_plus: 'Multiple Children Stay Free',
  clock_with_check: 'Guaranteed Late Checkout',
  clock_with_plus: 'Late Checkout / Early Check-In',
  crossed_utensils_with_plus: 'Dining Credit',
  dollar_sign_in_circle: 'Daily Dining Credit',
  lotus: 'Spa Treatment',
  martini_glass: 'Nightly Cocktail',
  p_in_circle: 'Parking',
}

function isTruthyString(value: string | undefined): value is string {
  return !!value?.trim()
}

function getPropertyRoomSizeSqm(size: string | undefined) {
  const sqmMatch = size?.match(/(\d+(?:\.\d+)?)\s*(?:m²|m2|sqm|sq m)/i)
  if (sqmMatch?.[1]) return Number(sqmMatch[1])

  const sqftMatch = size?.match(/(\d+(?:\.\d+)?)\s*(?:ft²|ft2|sqft|sq ft)/i)
  if (sqftMatch?.[1]) return Number(sqftMatch[1]) * 0.092903

  return undefined
}

function getBedbankRoomFeatureText(pkg: App.BedbankPackage, featureId: string) {
  return pkg.features.find(feature => feature.id === featureId)?.text
}

function getBedbankRoomSizeSqm(pkg: App.BedbankPackage) {
  return getPropertyRoomSizeSqm(getBedbankRoomFeatureText(pkg, 'dimension'))
}

function getBedbankRoomViewText(pkg: App.BedbankPackage) {
  return getBedbankRoomFeatureText(pkg, 'water')
}

function getPropertyRoomTexts(room: App.Property.Room) {
  return [
    room.description,
    ...room.popularAmenities.map(amenity => amenity.name),
    ...room.amenities.flatMap(amenityGroup => amenityGroup.items.map(amenity => amenity.name)),
  ].filter(isTruthyString)
}

function getPropertyFacilityNames(property: App.Property.Property) {
  return [
    ...property.popularFacilities.map(facility => facility.name),
    ...property.amenities.flatMap(amenityGroup => amenityGroup.items.map(amenity => amenity.name)),
  ].filter(isTruthyString)
}

function getPackageInclusionDescriptions(pkg: App.HotelPackage) {
  return [
    ...(pkg.inclusions ?? []),
    ...pkg.bonusInclusions,
    ...Object.values(pkg.luxPlusInclusionsByTier ?? {}).flat(),
  ].map(inclusion => inclusion.description)
}

function getRateInclusions(rate: App.EnrichedPropertyRate) {
  return [
    ...(rate.inclusions ?? []),
    ...(rate.bonusInclusions ?? []),
    ...(rate.luxPlusInclusions ?? []),
    ...(rate.bundleInclusions ?? []),
  ]
}

function hasBreakfastInclusion(inclusions: Array<App.OfferInclusion>) {
  return inclusions.some(inclusion => /\bbreakfast\b/i.test(inclusion.description))
}

function hasBreakfastText(textItems: Array<string>) {
  return textItems.some(text => /\bbreakfast\b/i.test(text))
}

function hasBreakfastIncludedOnAllPropertyRates(
  roomsWithRates: Array<App.Property.Room>,
  propertyRates: Array<App.EnrichedPropertyRate>,
) {
  if (propertyRates.length > 0) {
    return propertyRates.every(rate => hasBreakfastInclusion(getRateInclusions(rate)))
  }

  return roomsWithRates.length > 0 && roomsWithRates.every(room => hasBreakfastInclusion(getPropertyRoomInclusions(room)))
}

function hasBreakfastIncludedOnAllBedbankRates(rates: Array<App.BedbankRate>) {
  return rates.length > 0 && rates.every(rate => hasBreakfastInclusion(rate.inclusions))
}

function getPackageInclusionCategories(pkg: App.HotelPackage) {
  return [
    ...(pkg.inclusions ?? []),
    ...pkg.bonusInclusions,
    ...Object.values(pkg.luxPlusInclusionsByTier ?? {}).flat(),
  ].map(inclusion => inclusion.category).filter((category): category is App.PackageInclusionCategory => !!category)
}

function getOfferInclusionCategory(inclusion: App.OfferInclusion) {
  const category = (inclusion as { category?: App.PackageInclusionCategory }).category
  return category ?? (inclusion.symbol ? PROPERTY_INCLUSION_SYMBOL_CATEGORY_MAP[inclusion.symbol] : undefined)
}

function getPropertyRoomInclusions(room: App.Property.Room) {
  return [
    ...room.inclusions,
    ...room.bonusInclusions,
    ...room.luxPlusInclusions,
  ]
}

function getHotelPackageRoomTexts(pkg: App.HotelPackage) {
  return [
    pkg.roomType.description,
    ...pkg.roomType.amenities.map(amenity => amenity.description),
  ].filter(isTruthyString)
}

export function buildFlashHotelHighlightTagContext(
  offer: App.HotelOffer,
  property: App.OfferProperty,
  packageViews?: Array<App.OfferPackageOptionView>,
  charterBundleRoute?: App.CharterBundleRoute,
): OfferPageHighlightTagContext {
  const hasPackageViewContext = !!packageViews
  const packages = packageViews?.filter(view => view.hotelAvailable && view.capacityValid).map(view => view.package) ?? offer.packages
  const packageInclusionDescriptions = packages.flatMap(getPackageInclusionDescriptions)

  return {
    boardCodes: packages.map(pkg => pkg.boardCode),
    charterBundleRoute,
    facilities: [
      offer.facilities,
      offer.highlights,
      offer.gettingThere,
      property.address,
      property.geoData.locality,
      property.geoData.administrativeAreaLevel1,
    ].filter(isTruthyString),
    hasBreakfastIncludedOnAllRates: packages.length > 0 && packages.every(pkg => hasBreakfastText(getPackageInclusionDescriptions(pkg))),
    holidayTypes: offer.holidayTypes,
    inclusions: [
      ...(!hasPackageViewContext && offer.inclusions ? [offer.inclusions] : []),
      ...packageInclusionDescriptions,
    ].filter(isTruthyString),
    inclusionCategories: packages.flatMap(getPackageInclusionCategories),
    rating: property.rating,
    roomAmenities: packages.flatMap(getHotelPackageRoomTexts),
    roomNames: packages.map(pkg => pkg.roomType.name).filter(isTruthyString),
    roomSizeAvailableRoomCount: packages.length,
    roomSizesSqm: packages.map(pkg => pkg.roomType.sizeSqm).filter((size): size is number => typeof size === 'number'),
  }
}

export function buildPropertyHighlightTagContext(
  property: App.Property.Property,
  propertyRates: Array<App.EnrichedPropertyRate>,
): OfferPageHighlightTagContext {
  const rooms = Object.values(property.rooms)
  const availableRoomIds = new Set(propertyRates.map(rate => rate.roomId))
  const roomsWithRates = propertyRates.length > 0 ? rooms.filter(room => availableRoomIds.has(room.id)) : rooms
  const roomInclusions = roomsWithRates.flatMap(getPropertyRoomInclusions)
  const hasBreakfastIncludedOnAllRates = hasBreakfastIncludedOnAllPropertyRates(roomsWithRates, propertyRates)
  const facilityNames = getPropertyFacilityNames(property)

  return {
    boardCodes: propertyRates.map(rate => rate.boardCode),
    facilities: [
      property.highlights,
      property.locationDescription,
      property.facilities,
      property.location.title,
      property.location.formattedAddress,
      property.location.city,
      property.location.region,
      ...facilityNames,
    ].filter(isTruthyString),
    facilityNames,
    hasBreakfastIncludedOnAllRates,
    holidayTypes: property.holidayTypeTags,
    inclusions: roomInclusions.map(inclusion => inclusion.description),
    inclusionCategories: roomInclusions.map(getOfferInclusionCategory).filter((category): category is App.PackageInclusionCategory => !!category),
    locationTags: property.locationTags,
    rating: property.rating,
    roomAmenities: roomsWithRates.flatMap(getPropertyRoomTexts),
    roomNames: roomsWithRates.map(room => room.name).filter(isTruthyString),
    roomSizeAvailableRoomCount: roomsWithRates.length,
    roomSizesSqm: roomsWithRates.map(room => getPropertyRoomSizeSqm(room.size)).filter((size): size is number => typeof size === 'number'),
  }
}

export function buildBedbankHighlightTagContext(
  offer: App.BedbankOffer,
  hotelOnlyRates?: Array<App.BedbankRate>,
): OfferPageHighlightTagContext {
  const hasRateContext = !!hotelOnlyRates
  const packages = hotelOnlyRates?.map(rate => rate.room) ?? offer.packages
  const facilityNames = [
    ...offer.popularFacilities,
    ...offer.facilityGroups.flatMap(facilityGroup => facilityGroup.values.map(facility => facility.name)),
  ].filter(isTruthyString)

  return {
    boardCodes: hotelOnlyRates?.map(rate => rate.boardCode),
    facilities: [
      offer.amenities,
      offer.attractions,
      offer.description,
      offer.tagline,
      ...facilityNames,
    ].filter(isTruthyString),
    facilityNames,
    hasBreakfastIncludedOnAllRates: hasRateContext ?
        hasBreakfastIncludedOnAllBedbankRates(hotelOnlyRates) :
        hasBreakfastText((offer.inclusions ?? []).map(inclusion => inclusion.description)),
    holidayTypes: offer.holidayTypes ?? [],
    inclusions: hasRateContext ? [] : (offer.inclusions ?? []).map(inclusion => inclusion.description),
    rating: offer.property.rating,
    roomAmenities: packages.flatMap(pkg => [
      pkg.description,
      ...pkg.features.map(feature => feature.text),
      ...pkg.facilityGroups.flatMap(facilityGroup => facilityGroup.values.map(facility => facility.name)),
    ]).filter(isTruthyString),
    roomNames: packages.map(pkg => pkg.name).filter(isTruthyString),
    roomSizeAvailableRoomCount: packages.length,
    roomSizesSqm: packages.map(getBedbankRoomSizeSqm).filter((size): size is number => typeof size === 'number'),
    roomViewTags: packages.map(getBedbankRoomViewText).filter(isTruthyString),
  }
}
